/**
 * Nonce management for bot attestation flows
 *
 * Fetches short-lived nonces from the backend to prove client freshness.
 * Nonces are used to prevent replay attacks and verify legitimate interactions.
 */

import { API_ENDPOINT } from '../api-config.mjs';
import log from 'loglevel';

/**
 * Fetch a nonce from the bot attestation endpoint
 * @param {string} action - Optional action identifier (e.g., 'login', 'signup')
 * @returns {Promise<{nonce: string, expiresAt: number, action?: string}>}
 */
async function fetchNonceFromApi(action = null) {
  let url = `${API_ENDPOINT}/bot/nonce`;
  if (action) {
    url += `?action=${encodeURIComponent(action)}`;
  }
  const response = await fetch(url);
  if (!response.ok) {
    let error = {
      status: response.status,
      statusText: response.statusText,
    };
    error.responseBody = await response.text();
    log.warn("fetchNonceFromApi()", error);
    throw error;
  }
  const data = await response.json();

  // Validate response format
  if (!data.nonce || typeof data.nonce !== 'string') {
    log.error('[Nonce] Invalid response: missing or invalid nonce field', data);
    throw new Error('Invalid nonce response: missing nonce field');
  }

  if (!data.expiresAt || typeof data.expiresAt !== 'number') {
    log.error('[Nonce] Invalid response: missing or invalid expiresAt field', data);
    throw new Error('Invalid nonce response: missing expiresAt field');
  }

  // Check if expiresAt might be in seconds instead of milliseconds
  // A timestamp in seconds would be around 1.7 billion, milliseconds would be 1.7 trillion
  if (data.expiresAt < 10000000000) {
    log.error('[Nonce] expiresAt appears to be in seconds, not milliseconds:', {
      expiresAt: data.expiresAt,
      expectedRange: '>= 10000000000'
    });
    throw new Error('Invalid nonce response: expiresAt must be in milliseconds');
  }

  log.debug('[Nonce] Fetched nonce:', {
    action,
    expiresAt: data.expiresAt,
    expiresAtDate: new Date(data.expiresAt).toISOString(),
    ttlSeconds: Math.round((data.expiresAt - Date.now()) / 1000)
  });

  return data;
}

// Cache for nonces by action
const nonceCache = new Map();

// Track in-flight requests to prevent duplicate fetches
const inflightRequests = new Map();

// Minimum time before expiry to consider a nonce valid (5 seconds buffer)
const EXPIRY_BUFFER_MS = 5000;

// Maximum TTL to accept from server (5 minutes) - protects against malicious/buggy responses
const MAX_TTL_MS = 300000;

/**
 * Check if a cached nonce is still valid
 *
 * @param {object} cachedNonce - Cached nonce object
 * @returns {boolean}
 */
function isNonceValid(cachedNonce) {
  if (!cachedNonce) return false;

  // Validate expiresAt field exists and is a valid number
  if (!cachedNonce.expiresAt || typeof cachedNonce.expiresAt !== 'number') {
    log.warn('[Nonce] Invalid expiresAt:', cachedNonce.expiresAt);
    return false;
  }

  const now = Date.now();
  const timeUntilExpiry = cachedNonce.expiresAt - now;

  // Check if expired
  if (timeUntilExpiry < 0) {
    return false;
  }

  // Check if TTL is suspiciously long (could indicate wrong units or malicious response)
  if (timeUntilExpiry > MAX_TTL_MS) {
    log.warn('[Nonce] TTL too long:', {
      ttlSeconds: Math.round(timeUntilExpiry / 1000),
      maxTtlSeconds: MAX_TTL_MS / 1000
    });
    return false;
  }

  // Valid if more than EXPIRY_BUFFER_MS time remaining
  return timeUntilExpiry > EXPIRY_BUFFER_MS;
}

/**
 * Get a nonce - uses global cache shared across all actions
 * Uses cache if available and not expired, otherwise fetches a new one
 *
 * @param {string} action - Action identifier (e.g., 'login', 'signup', 'export') - for logging only
 * @param {boolean} forceRefresh - Force fetching a new nonce even if cached
 * @returns {Promise<string>} The nonce string
 */
export async function getNonce(action = null, forceRefresh = false) {
  const cacheKey = '__global__';  // Single cache for all actions

  // Check cache first (unless forcing refresh)
  if (!forceRefresh) {
    const cached = nonceCache.get(cacheKey);
    if (isNonceValid(cached)) {
      log.debug('[Nonce] Using cached nonce', action ? `(action: ${action})` : '');
      return cached.nonce;
    }
  }

  // Check if there's already a request in flight
  const inflightRequest = inflightRequests.get(cacheKey);
  if (inflightRequest) {
    log.debug('[Nonce] Waiting for in-flight request', action ? `(action: ${action})` : '');
    const nonceData = await inflightRequest;
    return nonceData.nonce;
  }

  // Start a new fetch and track it
  const fetchPromise = fetchNonceFromApi(action)
    .then(nonceData => {
      // Cache it globally
      nonceCache.set(cacheKey, nonceData);
      return nonceData;
    })
    .finally(() => {
      // Clean up in-flight tracking
      inflightRequests.delete(cacheKey);
    });

  inflightRequests.set(cacheKey, fetchPromise);
  const nonceData = await fetchPromise;

  return nonceData.nonce;
}

/**
 * Get nonce data (includes nonce, expiresAt, action)
 * Useful for debugging or advanced use cases
 *
 * @param {string} action - Action identifier (for logging only)
 * @param {boolean} forceRefresh - Force fetching a new nonce
 * @returns {Promise<{nonce: string, expiresAt: number, action: string}>}
 */
export async function getNonceData(action = null, forceRefresh = false) {
  const cacheKey = '__global__';  // Single cache for all actions

  // Check cache first
  if (!forceRefresh) {
    const cached = nonceCache.get(cacheKey);
    if (isNonceValid(cached)) {
      return cached;
    }
  }

  // Check if there's already a request in flight
  const inflightRequest = inflightRequests.get(cacheKey);
  if (inflightRequest) {
    log.debug('[Nonce] Waiting for in-flight request (data)', action ? `(action: ${action})` : '');
    return await inflightRequest;
  }

  // Start a new fetch and track it
  const fetchPromise = fetchNonceFromApi(action)
    .then(nonceData => {
      // Cache it globally
      nonceCache.set(cacheKey, nonceData);
      return nonceData;
    })
    .finally(() => {
      // Clean up in-flight tracking
      inflightRequests.delete(cacheKey);
    });

  inflightRequests.set(cacheKey, fetchPromise);
  return await fetchPromise;
}

/**
 * Prefetch a nonce for an action
 * Useful to warm the cache before a user action
 *
 * @param {string} action - Action identifier
 * @returns {Promise<void>}
 */
export async function prefetchNonce(action = null) {
  try {
    await getNonce(action, false);
    log.debug('[Nonce] Prefetched nonce for action:', action);
  } catch (error) {
    log.warn('[Nonce] Prefetch failed for action:', action, error);
  }
}

/**
 * Clear cached nonce (global cache)
 */
export function clearNonceCache() {
  const cacheKey = '__global__';
  nonceCache.delete(cacheKey);
  log.debug('[Nonce] Cleared global cache');
}

/**
 * Clear all cached nonces
 */
export function clearAllNonces() {
  nonceCache.clear();
  log.debug('[Nonce] Cleared all cached nonces');
}

/**
 * Get cache statistics (for debugging)
 *
 * @returns {object} Cache stats
 */
export function getNonceStats() {
  const stats = {
    cached: 0,
    expired: 0,
    valid: 0,
  };

  for (const [key, data] of nonceCache.entries()) {
    stats.cached++;
    if (isNonceValid(data)) {
      stats.valid++;
    } else {
      stats.expired++;
    }
  }

  return stats;
}
